// src/pages/auth/VerifyEmailRequired.jsx
//
// Hard gate shown by RequireEmailVerified for authenticated users
// whose email isn't verified yet. Looks identical to VerifyEmail.jsx
// but is wired into a different code path (route guard vs. post-
// signup landing). Polls every 2.5s for the verification flag to
// flip and auto-advances.
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { db } from '@/firebase/firebase';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
  AuthButtonSecondary,
  AuthBanner,
} from '@/components/auth/AuthShell';

export default function VerifyEmailRequired() {
  const auth = getAuth();
  const functions = getFunctions();
  const navigate = useNavigate();

  const ticking = useRef(false);
  const intervalRef = useRef(null);

  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [hasUser, setHasUser] = useState(!!auth.currentUser);

  const syncVerificationToFirestore = async (uid) => {
    // Canonical fields the useAuth hook + route guards understand.
    await setDoc(
      doc(db, 'users', uid),
      {
        emailVerified: true,
        trust: { emailVerified: true },
        onboardingStatus: 'email_verified',
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const onVerified = async (uid) => {
    try {
      await syncVerificationToFirestore(uid);
    } catch (e) {
      console.warn('[VerifyEmailRequired] Firestore sync failed (ignored):', e?.message);
    }
    toast.success('Email verified!');
    // /dashboard is the universal team picker post-Phase-1; routing
    // to /teams would bounce parent users via RequireCoachRole.
    navigate('/dashboard', { replace: true });
  };

  const checkNow = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;
    if (ticking.current) return;

    ticking.current = true;
    try {
      await u.reload();
      if (auth.currentUser?.emailVerified) {
        await onVerified(auth.currentUser.uid);
      }
    } catch (e) {
      console.warn('[VerifyEmailRequired] reload failed (ignored):', e?.message);
    } finally {
      ticking.current = false;
    }
  }, [auth]);

  useEffect(() => {
    intervalRef.current = setInterval(checkNow, 2500);

    const unsub = onAuthStateChanged(auth, async (u) => {
      setHasUser(!!u);
      if (u?.emailVerified) {
        await onVerified(u.uid);
      }
    });

    return () => {
      clearInterval(intervalRef.current);
      unsub();
    };
  }, [auth, checkNow]);

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      if (auth.currentUser) {
        const sendCustomVerification = httpsCallable(functions, 'sendVerificationEmail');
        await sendCustomVerification();
        toast.success('Verification email sent!');
      } else {
        setError('Please sign in again in this tab to resend the email.');
      }
    } catch (e) {
      setError(e?.message || 'Failed to resend verification email.');
    } finally {
      setResending(false);
    }
  };

  const email = auth.currentUser?.email || '(pending…)';

  return (
    <AuthShell cardWidth="narrow" showLegalFooter={false}>
      <div className="text-center">
        <AuthPill>Verify to continue</AuthPill>

        {/* Envelope icon */}
        <div className="mx-auto mt-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30">
          <svg
            className="h-7 w-7 text-[#b5f03d]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        <AuthHeading size="small">
          One last <span className="text-[#b5f03d]">step.</span>
        </AuthHeading>

        <AuthSubtitle>
          We sent a verification link to <strong className="text-white">{email}</strong>.
          This screen will continue automatically the moment you click it.
        </AuthSubtitle>
      </div>

      <div className="mt-7 space-y-3">
        {error && (
          <AuthBanner tone="error" onClose={() => setError('')}>
            {error}
          </AuthBanner>
        )}

        <AuthButton
          type="button"
          onClick={handleResend}
          disabled={resending}
          busy={resending}
        >
          {resending ? 'On it…' : 'Resend verification email'}
        </AuthButton>

        <AuthButtonSecondary type="button" onClick={checkNow}>
          I've verified — check again
        </AuthButtonSecondary>

        {!hasUser && (
          <AuthBanner tone="warn">
            Make sure you're signed in in this tab. If you verified in another
            browser or device, sign back in here so we can detect it.
          </AuthBanner>
        )}
      </div>
    </AuthShell>
  );
}
